/* ===========================================
  src/App.js - Con manejo de sesión persistente y optimizado para React 18
  =========================================== */
import React, { useEffect, useState, Suspense, lazy, useTransition } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async'; // Actualizado a react-helmet-async
import { AuthProvider } from './hooks/useAuth';
import { SavedItemsProvider } from './contexts/SavedItemsContext';
// CourseProgressProvider del sistema viejo de cursos quedó desmontado.
// Se mantiene en src/_unused-old-courses/... por si en algún momento queremos
// recuperarlo. Sin esto se simplifica el árbol de providers.
// import { CourseProgressProvider } from './contexts/CourseProgressContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { ActiveCompanyProvider } from './contexts/ActiveCompanyContext';
import { Toaster } from 'react-hot-toast';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ProtectedCompanyRoute from './components/routes/ProtectedCompanyRoute';
import Navbar from './components/common/Navbar';
import ScrollToTop from './components/common/ScrollToTop';
import IncompleteProfileBanner from './components/common/IncompleteProfileBanner';
import Analytics from './components/common/Analytics';
import PostHogProvider from './components/common/PostHogProvider';
import Footer from './components/common/Footer';
import supabase from './supabase';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NotificationInitializer from './components/common/NotificationInitializer';
import { JobProvider } from './contexts/JobContext';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiesPolicyPage from './pages/CookiesPolicyPage';
import TechnicianSearch from './components/member/TechnicianSearch';
import CompanyPublicWebsite from './pages/CompanyPublicWebsite'; // Corrected import path
import FlagBadgePreview from './pages/FlagBadgePreview';

// Componente de carga
const LoadingScreen = () => (
  <div className="flex flex-col min-h-screen items-center justify-center bg-dark-900">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
    <p className="mt-4 text-gray-400">Cargando...</p>
  </div>
);

// Error Boundary para capturar errores en los componentes
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error en el componente:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col min-h-screen items-center justify-center bg-dark-900 p-8 text-white">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Algo salió mal</h2>
          <p className="mb-6">Estamos trabajando para solucionarlo lo antes posible.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="py-3 px-6 bg-primary-600 hover:bg-primary-500 text-white font-bold rounded-lg transition-all duration-300"
          >
            Recargar la página
          </button>
          
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-8 p-4 bg-dark-800 rounded-lg max-w-3xl overflow-auto">
              <p className="font-bold mb-2">Error técnico:</p>
              <pre className="text-xs text-red-400">
                {this.state.error && this.state.error.toString()}
              </pre>
              <div className="mt-4">
                <p className="font-bold mb-2">Stack trace:</p>
                <pre className="text-xs text-gray-400 overflow-auto max-h-64">
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </div>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// Componente wrapper para componentes Lazy que usa startTransition
const SafeLazyLoad = ({ children }) => {
  const [isPending, startTransition] = useTransition();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    startTransition(() => {
      setMounted(true);
    });
  }, [startTransition]);

  if (!mounted || isPending) {
    return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  );
};

// Lazy loading de componentes pesados
const JobManagement = lazy(() => import('./pages/company/JobManagement'));
const NewsDetailPage = lazy(() => import('./components/common/NewsDetailPage'));
const CompanyChangePassword = lazy(() => import('./pages/company/CompanyChangePassword'));
const MessagesPage = lazy(() => import('./pages/messaging/MessagesPage'));
const CertificateView = lazy(() => import('./components/certificates/CertificateView'));
// CourseIntegration legacy (sistema viejo de cursos). Desmontado del routing.
// El archivo y sus dependencias viven en src/_unused-old-courses/. Si querés
// reactivarlo en el futuro, descomenta el import y el Route /courses/* abajo.
// const CourseIntegration = lazy(() => import('./components/courses/CourseIntegration'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));

// Páginas públicas
const HomePage = lazy(() => import('./pages/public/HomePage'));
const AboutPage = lazy(() => import('./pages/public/AboutPage'));
const MembershipPage = lazy(() => import('./pages/public/MembershipPage'));
const UniversityPage = lazy(() => import('./pages/public/UniversityPage'));
const OpportunitiesPage = lazy(() => import('./pages/public/OpportunitiesPage'));
const CalendarPage = lazy(() => import('./pages/public/CalendarPage'));
const NewsPage = lazy(() => import('./pages/public/NewsPage'));
const SponsorsPage = lazy(() => import('./pages/public/SponsorsPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const NotFoundPage = lazy(() => import('./pages/public/NotFoundPage'));
const PublicJobsList = lazy(() => import('./pages/public/PublicJobsList'));
const PublicJobView = lazy(() => import('./pages/public/PublicJobView'));
const EmpresasPage = lazy(() => import('./pages/public/EmpresasPage'));
const ProfesionalesPage = lazy(() => import('./pages/public/ProfesionalesPage'));
const PublicProfilePage = lazy(() => import('./pages/public/PublicProfilePage'));
const CompanySlugResolver = lazy(() => import('./pages/public/CompanySlugResolver'));
const ProfessionLandingPage = lazy(() => import('./pages/public/ProfessionLandingPage'));
const EmpleosHub = lazy(() => import('./pages/public/EmpleosHub'));
const CompanyLandingPage = lazy(() => import('./pages/public/CompanyLandingPage'));
const EmpresasAvHub = lazy(() => import('./pages/public/EmpresasAvHub'));
const UniversityActorLandingPage = lazy(() => import('./pages/public/UniversityActorLandingPage'));
const EspectroIndex = lazy(() => import('./pages/public/EspectroIndex'));
const EspectroEdicion = lazy(() => import('./pages/public/EspectroEdicion'));
const PublicFeedPage = lazy(() => import('./pages/public/PublicFeedPage'));
const PublicPostDetailPage = lazy(() => import('./pages/public/PublicPostDetailPage'));

// AVlink University - marketplace de cursos
const UniversityMarketplacePage = lazy(() => import('./pages/university/UniversityMarketplacePage'));
const UniversityCourseDetailPage = lazy(() => import('./pages/university/CourseDetailPage'));
const BecomeInstructorPage = lazy(() => import('./pages/university/BecomeInstructorPage'));
const UniversityInstructorPublicPage = lazy(() => import('./pages/university/InstructorPublicPage'));
const UniversityCategoryLandingPage = lazy(() => import('./pages/university/CategoryLandingPage'));
const UniversityCheckoutResultPage = lazy(() => import('./pages/university/CheckoutResultPage'));
const UniversityCertificateVerifyPage = lazy(() => import('./pages/university/CertificateVerifyPage'));
const UniversityLegalPage = lazy(() => import('./pages/university/UniversityLegalPage'));



// Al principio del archivo, agrega estos imports:



// Páginas de autenticación
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const AuthCallbackPage = lazy(() => import('./pages/auth/AuthCallbackPage'));
const ChooseTypePage = lazy(() => import('./pages/auth/ChooseTypePage'));
const CompleteProfilePage = lazy(() => import('./pages/auth/CompleteProfilePage'));
const TwoFactorSetupPage = lazy(() => import('./pages/auth/TwoFactorSetupPage'));

// Páginas de perfil
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const SecuritySettingsPage = lazy(() => import('./pages/profile/SecuritySettingsPage'));
const ChangePasswordPage = lazy(() => import('./pages/profile/ChangePasswordPage'));
// const CourseDetails = lazy(() => import('./pages/member/CourseDetails')); // legacy cursos viejos — sin Route activa

// Dashboards
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const GoldMemberDashboard = lazy(() => import('./components/member/GoldMemberDashboard'));
const SilverMemberDashboard = lazy(() => import('./components/member/SilverMemberDashboard'));
const BronzeMemberDashboard = lazy(() => import('./components/member/BronzeMemberDashboard'));
const CompanyDashboard = lazy(() => import('./pages/company/CompanyDashboard'));

// Secciones adicionales
// ExploreSection (legacy) — su tab Cursos consumía el sistema viejo de cursos.
// Desmontado del routing porque no había navegación viva apuntando a /explore.
// const ExploreSection = lazy(() => import('./components/member/sections/ExploreSection'));
const EventForm = lazy(() => import('./components/calendar/EventForm'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));

function AppRoutes() {
  const location = useLocation();
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [isPending, startTransition] = useTransition(); // Para manejar transiciones de estado

  // Verificar si hay una sesión activa al iniciar la aplicación
  useEffect(() => {
    const checkSession = async () => {
      try {
        await supabase.hasPersistedSession();
        // No necesitamos hacer nada aquí, la verificación real
        // se hace en el hook useAuth y en la página de login
      } catch (error) {
        console.error("Error verificando sesión:", error);
      } finally {
        // Usar startTransition para evitar errores de suspensión
        startTransition(() => {
          setIsCheckingSession(false);
        });
      }
    };

    // Añadir un timeout de seguridad de 3 segundos para evitar
    // que la pantalla de carga se quede atascada
    const timeoutId = setTimeout(() => {
      if (isCheckingSession) {
        console.warn("La verificación de sesión está tomando demasiado tiempo. Continuando...");
        startTransition(() => {
          setIsCheckingSession(false);
        });
      }
    }, 3000);

    checkSession();

    // Limpiar el timeout cuando el componente se desmonte o
    // cuando la verificación termine normalmente
    return () => clearTimeout(timeoutId);
  }, [isCheckingSession, startTransition]);

  // Mostrar spinner mientras se verifica la sesión
  if (isCheckingSession || isPending) {
    return <LoadingScreen />;
  }

  return (
    <div className="App flex flex-col min-h-screen">
      <ScrollToTop />
      <Analytics />
      <PostHogProvider />
      <IncompleteProfileBanner />
      <Routes>
        {/* Rutas protegidas con ProtectedRoute */}







        {/* Página pública de noticia individual (con Navbar y Footer como el resto del sitio) */}
        <Route path="/news/:articleId" element={
          <>
            <Navbar />
            <main className="flex-grow">
              <Suspense fallback={<LoadingScreen />}>
                <SafeLazyLoad>
                  <NewsDetailPage context="public" />
                </SafeLazyLoad>
              </Suspense>
            </main>
            <Footer />
          </>
        } />



        <Route path="/admin/*" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Suspense fallback={<LoadingScreen />}>
              <SafeLazyLoad>
                <AdminDashboard />
              </SafeLazyLoad>
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/admin/events/new" element={
          <Suspense fallback={<LoadingScreen />}>
            <SafeLazyLoad>
              <EventForm />
            </SafeLazyLoad>
          </Suspense>
        } />
        <Route path="/gold-member/*" element={
          <ProtectedRoute allowedRoles={['individual']} allowedMemberships={['gold']}>
            <Suspense fallback={<LoadingScreen />}>
              <SafeLazyLoad>
                <GoldMemberDashboard />
              </SafeLazyLoad>
            </Suspense>
          </ProtectedRoute>
        } />

        <Route path="/company/*" element={
          <ProtectedCompanyRoute>
            <Suspense fallback={<LoadingScreen />}>
              <SafeLazyLoad>
                <CompanyDashboard />
              </SafeLazyLoad>
            </Suspense>
          </ProtectedCompanyRoute>
        } />

        <Route path="/company/profile/change-password" element={
          <ProtectedCompanyRoute>
            <Suspense fallback={<LoadingScreen />}>
              <SafeLazyLoad>
                <CompanyChangePassword />
              </SafeLazyLoad>
            </Suspense>
          </ProtectedCompanyRoute>
        } />

        <Route path="/messages" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingScreen />}>
              <SafeLazyLoad>
                <MessagesPage />
              </SafeLazyLoad>
            </Suspense>
          </ProtectedRoute>
        } />

        <Route path="/silver-member/*" element={
          <ProtectedRoute allowedRoles={['individual']} allowedMemberships={['gold', 'silver']}>
            <Suspense fallback={<LoadingScreen />}>
              <SafeLazyLoad>
                <SilverMemberDashboard />
              </SafeLazyLoad>
            </Suspense>
          </ProtectedRoute>
        } />

        {/* Página pública de empresa */}
        <Route path="/empresa/:slug" element={<CompanyPublicWebsite />} />

        {/* Dev: preview de FlagBadge (no entra a sitemap/pre-render) */}
        <Route path="/flag-preview" element={<FlagBadgePreview />} />

        <Route path="/bronze-member/*" element={
          <ProtectedRoute allowedRoles={['individual']} previewForAnonymous>
            <Suspense fallback={<LoadingScreen />}>
              <SafeLazyLoad>
                <BronzeMemberDashboard />
              </SafeLazyLoad>
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/company/*" element={
          <ProtectedCompanyRoute>
            <JobProvider>
              <Suspense fallback={<LoadingScreen />}>
                <SafeLazyLoad>
                  <CompanyDashboard />
                </SafeLazyLoad>
              </Suspense>
            </JobProvider>
          </ProtectedCompanyRoute>
        } />

        <Route path="/member/technicians" element={<ProtectedRoute><TechnicianSearch /></ProtectedRoute>} />

        {/* Ruta `/courses/*` (sistema viejo de cursos) desmontada.
            Los cursos del marketplace AVlink University están en
            `/universidad/curso/:slug` y `/bronze-member/courses/*`. */}

        {/* Páginas de perfil (protegidas) */}
        <Route path="/profile" element={
          <ProtectedRoute>
            <>
              <Navbar />
              <main className="flex-grow">
                <Suspense fallback={<LoadingScreen />}>
                  <SafeLazyLoad>
                    <ProfilePage />
                  </SafeLazyLoad>
                </Suspense>
              </main>
              <Footer />
            </>
          </ProtectedRoute>
        } />

        <Route path="/profile/:userId" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingScreen />}>
              <SafeLazyLoad>
                <UserProfilePage />
              </SafeLazyLoad>
            </Suspense>
          </ProtectedRoute>
        } />

        <Route path="/settings/security" element={
          <ProtectedRoute>
            <>
              <Navbar />
              <main className="flex-grow">
                <Suspense fallback={<LoadingScreen />}>
                  <SafeLazyLoad>
                    <SecuritySettingsPage />
                  </SafeLazyLoad>
                </Suspense>
              </main>
              <Footer />
            </>
          </ProtectedRoute>
        } />


        <Route path="/settings/security" element={
          <ProtectedRoute>
            <>
              <Navbar />
              <main className="flex-grow">
                <Suspense fallback={<LoadingScreen />}>
                  <SafeLazyLoad>
                    <SecuritySettingsPage />
                  </SafeLazyLoad>
                </Suspense>
              </main>
              <Footer />
            </>
          </ProtectedRoute>
        } />
        <Route path="/settings/security/change-password" element={
          <ProtectedRoute>
            <>
              <Navbar />
              <main className="flex-grow">
                <Suspense fallback={<LoadingScreen />}>
                  <SafeLazyLoad>
                    <ChangePasswordPage />
                  </SafeLazyLoad>
                </Suspense>
              </main>
              <Footer />
            </>
          </ProtectedRoute>
        } />
        <Route path="/settings/security/2fa-setup" element={
          <ProtectedRoute>
            <>
              <Navbar />
              <main className="flex-grow">
                <Suspense fallback={<LoadingScreen />}>
                  <SafeLazyLoad>
                    <TwoFactorSetupPage />
                  </SafeLazyLoad>
                </Suspense>
              </main>
              <Footer />
            </>
          </ProtectedRoute>
        } />

        {/* Resto de rutas públicas con navbar/footer */}
        <Route path="/terms" element={
          <>
            <Navbar />
            <main className="flex-grow">
              <Suspense fallback={<LoadingScreen />}>
                <SafeLazyLoad>
                  <TermsAndConditionsPage />
                </SafeLazyLoad>
              </Suspense>
            </main>
            <Footer />
          </>
        } />

        {/* Páginas legales bajo /legal/* — referenciadas desde el Footer. */}
        <Route path="/legal/terms" element={
          <>
            <Navbar />
            <main className="flex-grow">
              <Suspense fallback={<LoadingScreen />}>
                <SafeLazyLoad>
                  <TermsAndConditionsPage />
                </SafeLazyLoad>
              </Suspense>
            </main>
            <Footer />
          </>
        } />
        <Route path="/legal/privacy" element={
          <>
            <Navbar />
            <main className="flex-grow">
              <Suspense fallback={<LoadingScreen />}>
                <SafeLazyLoad>
                  <PrivacyPolicyPage />
                </SafeLazyLoad>
              </Suspense>
            </main>
            <Footer />
          </>
        } />
        <Route path="/legal/cookies" element={
          <>
            <Navbar />
            <main className="flex-grow">
              <Suspense fallback={<LoadingScreen />}>
                <SafeLazyLoad>
                  <CookiesPolicyPage />
                </SafeLazyLoad>
              </Suspense>
            </main>
            <Footer />
          </>
        } />


        <Route path="/*" element={
          <>
            <Navbar />
            <main className="flex-grow">
              <Routes>
                {/* Rutas públicas */}
                <Route path="/dashboard/news/:articleId" element={
                  <ProtectedRoute>
                    <>
                      <Navbar />
                      <main className="flex-grow">
                        <Suspense fallback={<LoadingScreen />}>
                          <SafeLazyLoad>
                            <NewsDetailPage context="user" />
                          </SafeLazyLoad>
                        </Suspense>
                      </main>
                      <Footer />
                    </>
                  </ProtectedRoute>
                } />


                {/* Rutas para usuarios */}
                <Route path="/dashboard/news/:articleId" element={
                  <ProtectedRoute>
                    <>
                      <Navbar />
                      <main className="flex-grow">
                        <Suspense fallback={<LoadingScreen />}>
                          <SafeLazyLoad>
                            <NewsDetailPage context="user" />
                          </SafeLazyLoad>
                        </Suspense>
                      </main>
                      <Footer />
                    </>
                  </ProtectedRoute>
                } />

                {/* Rutas para empresas */}
                <Route path="/company/news/:articleId" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LoadingScreen />}>
                      <SafeLazyLoad>
                        <NewsDetailPage context="company" />
                      </SafeLazyLoad>
                    </Suspense>
                  </ProtectedRoute>
                } />

                {/* Resto de rutas existentes */}
                <Route path="/" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <HomePage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/about" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <AboutPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/membership" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <MembershipPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/empresas" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <EmpresasPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/profesionales" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <ProfesionalesPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                {/* Perfil público de profesional individual (opt-in vía settings de privacidad) */}
                <Route path="/u/:userId" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <PublicProfilePage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/university" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <UniversityPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                {/* Avlink University: landings por actor (instructor /
                    centro-educativo), país y ciudad. Mismo modelo que
                    /empleos y /empresas-av. */}
                <Route path="/university/:actor" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <UniversityActorLandingPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/university/:actor/:country" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <UniversityActorLandingPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/university/:actor/:country/:city" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <UniversityActorLandingPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/opportunities" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <OpportunitiesPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/calendar" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <CalendarPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/news" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <NewsPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/sponsors" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <SponsorsPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/contact" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <ContactPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                {/* Empleos: tanto /jobs como /jobs/:id redirigen al dashboard real.
                    Toda superficie publica pasa por /bronze-member, donde anonimos ven
                    el dashboard con el overlay de registro encima. El SEO de /jobs
                    se mantiene gracias al noscript pre-renderizado (build/jobs/index.html)
                    que Vercel sirve antes del catch-all. */}
                <Route path="/jobs" element={<Navigate to="/bronze-member/jobs" replace />} />
                <Route path="/jobs/:id" element={<Navigate to="/bronze-member/jobs" replace />} />
                <Route path="/certificates/:certificateId" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <CertificateView />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                {/* Route /explore (ExploreSection) desmontada — legacy de cursos viejos */}
                <Route path="/register" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <RegisterPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/login" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <LoginPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/sitemap" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <SitemapPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/forgot-password" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <ForgotPasswordPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/reset-password" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <ResetPasswordPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/auth/verify" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <VerifyEmailPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                {/* Callback de OAuth (Google). Procesa el hash, crea el perfil si hace falta,
                    redirige al dashboard correspondiente. */}
                <Route path="/auth/callback" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <AuthCallbackPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                {/* Selector de tipo de cuenta post-login para usuarios sin perfil */}
                <Route path="/auth/choose-type" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <ChooseTypePage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                {/* Onboarding soft: pide datos minimos (nombre, apellido, pais, edad
                    para tecnicos; nombre-empresa+pais para empresas). Redirect
                    automatico desde LoginPage si el perfil esta incompleto. */}
                <Route path="/complete-profile" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LoadingScreen />}>
                      <SafeLazyLoad>
                        <CompleteProfilePage />
                      </SafeLazyLoad>
                    </Suspense>
                  </ProtectedRoute>
                } />
                {/* Hub principal de empleos: lista los 44 rubros agrupados
                    por área. Debe ir antes que /empleos/:role para que
                    /empleos resuelva al hub y no a la landing del rol "empleos". */}
                <Route path="/empleos" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <EmpleosHub />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                {/* Landing SEO por rubro profesional. DEBE ir antes de /:slug
                    porque si no /empleos cae en CompanySlugResolver.
                    Variante con país: /empleos/<role>/<country> (Fase 2). */}
                <Route path="/empleos/:role" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <ProfessionLandingPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/empleos/:role/:country" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <ProfessionLandingPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/empleos/:role/:country/:city" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <ProfessionLandingPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                {/* Directorio B2B de empresas AV. Mismo orden que /empleos:
                    hub, tipo, tipo+pais, tipo+pais+ciudad. Antes de /:slug. */}
                <Route path="/empresas-av" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <EmpresasAvHub />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/empresas-av/:type" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <CompanyLandingPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/empresas-av/:type/:country" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <CompanyLandingPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/empresas-av/:type/:country/:city" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <CompanyLandingPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                {/* Feed y post: redirigen al dashboard real. El dashboard tiene su propio
                    feed (MemberHomeFeed) y se muestra con overlay de registro para anonimos.
                    Mantenemos las URLs viejas como redirects para SEO/inbound links. */}
                <Route path="/feed" element={<Navigate to="/bronze-member" replace />} />
                <Route path="/feed/:categoria" element={<Navigate to="/bronze-member" replace />} />
                <Route path="/post/:postId" element={<Navigate to="/bronze-member" replace />} />
                {/* AVlink University Marketplace - publico (catalogo de cursos).
                    Deben ir ANTES de /:slug para no caer en CompanySlugResolver. */}
                <Route path="/universidad" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <UniversityMarketplacePage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/universidad/curso/:slug" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <UniversityCourseDetailPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/universidad/convertirse-instructor" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <BecomeInstructorPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/universidad/instructor/:idOrSlug" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <UniversityInstructorPublicPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/universidad/categoria/:slug" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <UniversityCategoryLandingPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/universidad/checkout/exito" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <UniversityCheckoutResultPage outcome="success" />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/universidad/checkout/cancelado" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <UniversityCheckoutResultPage outcome="cancelled" />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/universidad/certificado/:code" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <UniversityCertificateVerifyPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/legal/universidad/:section" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <UniversityLegalPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />

                {/* AVlink Espectro: semanario editorial. Hub + edición individual.
                    Deben ir ANTES de /:slug para no caer en CompanySlugResolver. */}
                <Route path="/espectro" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <EspectroIndex />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="/espectro/:slug" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <EspectroEdicion />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                {/* Resolver de slug raíz: si /<slug> coincide con una empresa publicada, la renderiza.
                    Esta ruta debe ir DESPUÉS de todas las rutas estáticas y antes del catch-all 404. */}
                <Route path="/:slug" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <CompanySlugResolver />
                    </SafeLazyLoad>
                  </Suspense>
                } />
                <Route path="*" element={
                  <Suspense fallback={<LoadingScreen />}>
                    <SafeLazyLoad>
                      <NotFoundPage />
                    </SafeLazyLoad>
                  </Suspense>
                } />
              </Routes>
            </main>
            <Footer />
          </>
        } />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider> {/* Cambio de Helmet a HelmetProvider */}
      <ErrorBoundary>
        <Router>
          <AuthProvider>
            {/* Añadir NotificationInitializer justo después de AuthProvider */}
            <NotificationInitializer />
            
            <NotificationsProvider options={{ lazyLoad: true, timeout: 8000 }}>
              <ActiveCompanyProvider>
              <SavedItemsProvider>
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: '#1e1e2d',
                        color: '#fff',
                        border: '1px solid #2a2a3a'
                      }
                    }}
                  />
                  <ToastContainer
                    position="bottom-center"
                    autoClose={4000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="dark" // Usa colores oscuros base, pero lo podemos personalizar más abajo
                    toastStyle={{
                      background: '#1e1e2d',      // Fondo del toast
                      color: '#ffffff',           // Texto blanco
                      border: '1px solid #2a2a3a',// Borde sutil
                      fontSize: '14px',
                      borderRadius: '8px'
                    }}
                    bodyStyle={{
                      fontFamily: 'Inter, sans-serif',
                      padding: '8px 12px'
                    }}
                    progressStyle={{
                      background: '#00d6b4' // Color de la barra de progreso (puede ser tu color primario)
                    }}
                  />

                  <AppRoutes />
              </SavedItemsProvider>
              </ActiveCompanyProvider>
            </NotificationsProvider>
          </AuthProvider>
        </Router>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
